import _getAppName from "../functions/helpers/getAppName.js";
import _ttmlFunc from "../functions/profiles/ttmlGeneral.js";
import { XMLParser as _XMLParser } from "fast-xml-parser";
import _eol from "eol";
import _convertToPlainTextCustom from "../functions/quill/convertToPlainTextCustom.js";
import _convertToPlainText from "../functions/quill/convertToPlainText.js";
import _getFormatOptions from "../functions/helpers/getFormatOptions.js";
import _removeInvalidEvents from "../functions/eventGroups/removeInvalidEvents.js";
import _tcLib from "../lib/timecode.js";
const tcLib = _tcLib;
const removeInvalidEvents = _removeInvalidEvents;
const getFormatOptions = _getFormatOptions;
const convertToPlainText = _convertToPlainText;
const convertToPlainTextCustom = _convertToPlainTextCustom;
const eol = _eol;
const XMLParser = _XMLParser; //Decode
const ttmlFunc = _ttmlFunc;
const getAppName = _getAppName;

/* IMSC 1.1 Text Profile: https://www.w3.org/TR/ttml-imsc1.1/ */
/* A valid IMSC 1.0.1 Text Profile document is also a valid IMSC 1.1 Text Profile document, */
/* so decoding can reuse the same generic TTML parsing used by the other IMSC/TTML profiles. */
export default {
  decode: function (input, options) {
    let events = [],
      regions = [],
      styles = [],
      region,
      style,
      timeBase = "media",
      useClockTime = false;
    const xmlToJson = new XMLParser({
      stopNodes: ["*.p"],
      numberParseOptions: {
        skipLike: /[0-9]+/
      },
      ignoreAttributes: false
    });
    let fileJson = xmlToJson.parse(input);
    //if the fileJson is missing the tt element we need to add it in
    if (!fileJson.tt) {
      fileJson = {
        tt: fileJson
      };
    }
    timeBase = ttmlFunc.getTimeBaseValue(input) || "media";
    useClockTime = timeBase === "media" || timeBase === "clock" ? true : false;

    /* Put the region data into an array */
    if (fileJson.tt.head.layout && fileJson.tt.head.layout.region) {
      if (Array.isArray(fileJson.tt.head.layout.region)) {
        regions = fileJson.tt.head.layout.region;
      } else {
        regions = [fileJson.tt.head.layout.region];
      }
    }

    /* Put the style data into an array */
    if (fileJson.tt.head.styling && fileJson.tt.head.styling.style) {
      if (Array.isArray(fileJson.tt.head.styling.style)) {
        styles = fileJson.tt.head.styling.style;
      } else {
        styles = [fileJson.tt.head.styling.style];
      }
    }
    if (Array.isArray(fileJson.tt.body.div)) {
      fileJson.tt.body.div.forEach(paragraphGroup => {
        if (Array.isArray(paragraphGroup.p)) {
          paragraphGroup.p.forEach(paragraph => {
            if (paragraph["@_region"]) {
              region = regions.find(regionEl => {
                return regionEl["@_xml:id"] === paragraph["@_region"];
              });
            } else {
              region = false;
            }
            if (paragraph["@_style"]) {
              style = styles.find(styleEl => {
                return styleEl["@_xml:id"] === paragraph["@_style"];
              });
            } else {
              style = false;
            }
            events.push(ttmlFunc.multiLine.decodeSubtitle(paragraph, region, style, options.frameRate, options.dropFrame, useClockTime, options.window));
          });
        } else {
          if (paragraphGroup.p["@_region"]) {
            region = regions.find(regionEl => {
              return regionEl["@_xml:id"] === paragraphGroup.p["@_region"];
            });
          } else {
            region = false;
          }
          if (paragraphGroup.p["@_style"]) {
            style = styles.find(styleEl => {
              return styleEl["@_xml:id"] === paragraphGroup.p["@_style"];
            });
          } else {
            style = false;
          }
          events.push(ttmlFunc.multiLine.decodeSubtitle(paragraphGroup.p, region, style, options.frameRate, options.dropFrame, useClockTime, options.window));
        }
      });
    } else {
      if (Array.isArray(fileJson.tt.body.div.p)) {
        fileJson.tt.body.div.p.forEach(paragraph => {
          if (paragraph["@_region"]) {
            region = regions.find(regionEl => {
              return regionEl["@_xml:id"] === paragraph["@_region"];
            });
          } else {
            region = false;
          }
          if (paragraph["@_style"]) {
            style = styles.find(styleEl => {
              return styleEl["@_xml:id"] === paragraph["@_style"];
            });
          } else {
            style = false;
          }
          events.push(ttmlFunc.multiLine.decodeSubtitle(paragraph, region, style, options.frameRate, options.dropFrame, useClockTime, options.window));
        });
      } else {
        let paragraph = fileJson.tt.body.div.p;
        if (paragraph["@_region"]) {
          region = regions.find(regionEl => {
            return regionEl["@_xml:id"] === paragraph["@_region"];
          });
        } else {
          region = false;
        }
        if (paragraph["@_style"]) {
          style = styles.find(styleEl => {
            return styleEl["@_xml:id"] === paragraph["@_style"];
          });
        } else {
          style = false;
        }
        events.push(ttmlFunc.multiLine.decodeSubtitle(paragraph, region, style, options.frameRate, options.dropFrame, useClockTime, options.window));
      }
    }
    return events;
  },
  encode: function (eventGroup, options) {
    let encodingOptions = getFormatOptions(options.formatOptions);
    let timeBase = encodingOptions["Time Base"] || "media";
    let tcFormat = encodingOptions["Timecode Format"] || "smpte";
    let frameRateMultiplier = ttmlFunc.frameRateMultiplierMap[options.frameRate];
    let absolutePosition = encodingOptions["Absolute Position"] === true || encodingOptions["Absolute Position"] === "true";
    let fontColor = encodingOptions["Font Color"] || "white";
    let backgroundColor = encodingOptions["Background Color"] || "black";
    let outlineColor = encodingOptions["Text Outline Color"] || "";
    let textOutlineAttr = outlineColor ? ` tts:textOutline="${outlineColor} ${encodingOptions["Text Outline Size"] || "2"}${encodingOptions["Text Outline Size Unit"] || "px"}"` : "";
    let baseStyleAttrs = `tts:color="${fontColor}" tts:backgroundColor="${backgroundColor}" tts:fontFamily="${encodingOptions["Font Family"] || "monospaceSerif"}" tts:fontSize="${encodingOptions["Font Size"] || "80"}%" tts:fontWeight="${encodingOptions["Font Weight"] || "normal"}" tts:fontStyle="${encodingOptions["Font Style"] || "normal"}" tts:lineHeight="${encodingOptions["Line Height"] || "100"}%"${textOutlineAttr}`;
    let regions = "";
    let paragraphs = "";
    let styling = "";
    if (absolutePosition) {
      /* Shared safe-area regions (deduped) + alignment-based styles, modeled on the IMSC 1.1 example layout */
      const lineExtent = 7.33; // Height per line of text (%)
      const rowHeight = 80 / (options.window.height || 15); // Height of a single caption row within the 80% safe area
      let regionMap = new Map();
      let alignmentsUsed = new Set();
      eventGroup.events.forEach(event => {
        let plainTextCustom = convertToPlainTextCustom(event.text, "\n", false, "strong", "em", "u", true);
        let ttmlText = ttmlFunc.multiLine.convertToTtml(plainTextCustom);
        let plainText = convertToPlainText(event.text);
        let start = tcFormat === "smpte" ? tcLib.secToTcClock(event.start, options.frameRate, options.dropFrame) : tcLib.secToTcMs(event.start, options.frameRate, options.dropFrame).replace(",", ".");
        let end = tcFormat === "smpte" ? tcLib.secToTcClock(event.end, options.frameRate, options.dropFrame) : tcLib.secToTcMs(event.end, options.frameRate, options.dropFrame).replace(",", ".");
        let alignment = ["left", "center", "right"].includes(event.alignment) ? event.alignment : "center";
        alignmentsUsed.add(alignment);
        let regionId, regionMarkup;
        let lineCount = eol.split(plainText).length;
        if (event.yPos === "start") {
          regionId = "top";
          regionMarkup = `<region xml:id="top" tts:displayAlign="before" tts:origin="10% 10%" tts:extent="80% 80%"></region>`;
        } else if (event.yPos === "center") {
          regionId = "center";
          regionMarkup = `<region xml:id="center" tts:displayAlign="center" tts:origin="10% 10%" tts:extent="80% 80%"></region>`;
        } else if (!isNaN(parseInt(event.yPos, 10))) {
          /* Numeric line position: anchor the region so its bottom edge sits on the event's last line */
          let startLine = parseInt(event.yPos, 10);
          let endLine = startLine + lineCount - 1;
          let extentHeight = parseFloat((lineCount * lineExtent).toFixed(2));
          let yOrigin = parseFloat((10 + endLine * rowHeight - extentHeight).toFixed(2));
          regionId = lineCount > 1 ? `region.${startLine}_${endLine}` : `region.${startLine}`;
          regionMarkup = `<region xml:id="${regionId}" tts:displayAlign="after" tts:origin="10% ${yOrigin}%" tts:extent="80% ${extentHeight}%"></region>`;
        } else {
          regionId = "bottom";
          regionMarkup = `<region xml:id="bottom" tts:displayAlign="after" tts:origin="10% 10%" tts:extent="80% 80%"></region>`;
        }
        if (!regionMap.has(regionId)) {
          regionMap.set(regionId, regionMarkup);
        }
        paragraphs += eol.after(`<div>`);
        paragraphs += eol.after(`<p begin="${start.replace(";", ":")}" end="${end.replace(";", ":")}" style="basic.${alignment}" region="${regionId}"${ttmlFunc.forcedDisplayAttr(event)}>${ttmlText}</p>`);
        paragraphs += eol.after(`</div>`);
      });
      regionMap.forEach(regionMarkup => {
        regions += eol.after(regionMarkup);
      });
      alignmentsUsed.forEach(alignment => {
        styling += eol.after(`        <style xml:id="basic.${alignment}" ${baseStyleAttrs} tts:textAlign="${alignment}"/>`);
      });
    } else {
      styling = eol.after(`        <style xml:id="basic" ${baseStyleAttrs}/>`);
      eventGroup.events.forEach((event, index) => {
        let plainTextCustom = convertToPlainTextCustom(event.text, "\n", false, "strong", "em", "u", true);
        let ttmlText = ttmlFunc.multiLine.convertToTtml(plainTextCustom);
        let plainText = convertToPlainText(event.text);
        let start = tcFormat === "smpte" ? tcLib.secToTcClock(event.start, options.frameRate, options.dropFrame) : tcLib.secToTcMs(event.start, options.frameRate, options.dropFrame).replace(",", ".");
        let end = tcFormat === "smpte" ? tcLib.secToTcClock(event.end, options.frameRate, options.dropFrame) : tcLib.secToTcMs(event.end, options.frameRate, options.dropFrame).replace(",", ".");
        let regionId = `${event.style}_${index + 1}`;
        let style = "basic";
        let extents = ttmlFunc.multiLine.calcExtents(plainText, encodingOptions["Font Size"]);
        let origins = ttmlFunc.multiLine.calcOrigin(plainText, event.xPos, event.yPos, encodingOptions["Font Size"], options.window);
        let alignment = event.alignment;
        regions += eol.after(`<region xml:id="${regionId}" tts:showBackground="whenActive" tts:origin="${origins}" tts:extent="${extents}" tts:textAlign="${alignment}" tts:overflow="visible" tts:wrapOption="noWrap"></region>`);
        paragraphs += eol.after(`<div>`);
        paragraphs += eol.after(`<p begin="${start.replace(";", ":")}" end="${end.replace(";", ":")}" style="${style}" region="${regionId}"${ttmlFunc.forcedDisplayAttr(event)}>${ttmlText}</p>`);
        paragraphs += eol.after(`</div>`);
      });
    }
    let output = eol.after(`<?xml version="1.0" encoding="UTF-8"?>`);
    output += eol.after(`<tt xml:lang="${encodingOptions["Language Code"] || "en"}" xmlns="http://www.w3.org/ns/ttml" xmlns:tts="http://www.w3.org/ns/ttml#styling" xmlns:ttm="http://www.w3.org/ns/ttml#metadata" xmlns:ttp="http://www.w3.org/ns/ttml#parameter" xmlns:smpte="http://www.smpte-ra.org/schemas/2052-1/2010/smpte-tt" xmlns:m608="http://www.smpte-ra.org/schemas/2052-1/2010/smpte-tt#cea608" ttp:timeBase="${timeBase}" ttp:frameRate="${ttmlFunc.frameRateMap[options.frameRate]}" ttp:frameRateMultiplier="${frameRateMultiplier}" ttp:contentProfiles="http://www.w3.org/ns/ttml/profile/imsc1.1/text" xmlns:ebutts="urn:ebu:tt:style" xmlns:itts="http://www.w3.org/ns/ttml/profile/imsc1#styling" xmlns:ittp="http://www.w3.org/ns/ttml/profile/imsc1#parameter" xmlns:ittm="http://www.w3.org/ns/ttml/profile/imsc1#metadata" ittp:aspectRatio="${encodingOptions["Aspect Ratio"] === "4:3" ? "4 3" : "16 9"}">`);
    output += eol.after(`<head>`);
    output += eol.after(`<metadata>
        <ttm:title>${encodingOptions["Program Name"] || "Untitled"}</ttm:title>
        <ttm:desc>IMSC 1.1 Text Profile document created by ${getAppName()}</ttm:desc>
        <smpte:information xmlns:m608="http://www.smpte-ra.org/schemas/2052-1/2010/smpte-tt#cea608" origin="http://www.smpte-ra.org/schemas/2052-1/2010/smpte-tt#cea608" mode="Preserved" m608:channel="CC1" m608:programName="${encodingOptions["Program Name"] || "Untitled"}" m608:captionService="F1C1CC"/>
    </metadata>`);
    output += eol.after(`<styling>`);
    output += styling;
    output += eol.after(`    </styling>`);
    output += eol.after(`<layout>`);
    /* Regions Go Here: */
    output += regions;
    output += eol.after(`</layout>`);
    output += eol.after(`</head>`);
    output += eol.after(`<body>`);
    output += paragraphs;
    output += eol.after(`</body>`);
    output += `</tt>`;
    return output;
  },
  preProcess: {
    encode: function (eventGroup) {
      return removeInvalidEvents(eventGroup);
    },
    decode: function (input) {
      return eol.lf(input);
    }
  },
  postProcess: {
    encode: function (output) {
      return output;
    },
    decode: function (eventGroup) {
      return eventGroup;
    }
  }
};